import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  addEventToQueue, 
  incrementPageViews,
  updateLastActivity,
  initializeSession,
  selectSessionData
} from '../../../app/store/slices/analyticsSlice';
import analyticsService from '../services/AnalyticsService';
import { ROUTE_MAPPING } from '../utils/RouteMapping';

const RouteTracker = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector(state => state.auth.user);
  const sessionData = useSelector(selectSessionData);

  // Initialize analytics service
  useEffect(() => {
    if (!analyticsService.isInitialized) {
      analyticsService.init();
    }
  }, []);

  // Initialize session when component mounts
  useEffect(() => {
    if (!sessionData?.sessionId) {
      const sessionId = Date.now() + '-' + Math.random().toString(36).substr(2, 9);
      dispatch(initializeSession({
        sessionId,
        startTime: Date.now()
      }));
    }
  }, [dispatch, sessionData?.sessionId]);

  // Set user when available
  useEffect(() => {
    if (user?.id) {
      analyticsService.setUser(user.id, {
        role: user.role,
        plan: user.plan,
        isEmailVerified: user.isEmailVerified
      });
    }
  }, [user]);

  // Track page views
  useEffect(() => {
    const routeInfo = ROUTE_MAPPING[location.pathname] || { 
      name: 'Unknown Page', 
      category: 'other',
      priority: 'low'
    };
    
    // Track via analytics service
    analyticsService.trackPageView(location.pathname, {
      routeName: routeInfo.name,
      routeCategory: routeInfo.category,
      priority: routeInfo.priority,
      search: location.search,
      hash: location.hash
    });

    // Also dispatch to Redux for immediate state updates
    dispatch(addEventToQueue({
      eventName: 'page_view',
      properties: {
        userId: user?.id,
        sessionId: sessionData?.sessionId,
        path: location.pathname,
        routeName: routeInfo.name,
        routeCategory: routeInfo.category,
        search: location.search,
        hash: location.hash,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        referrer: document.referrer
      }
    }));

    dispatch(incrementPageViews());
    dispatch(updateLastActivity());

  }, [location, user?.id, sessionData?.sessionId, dispatch]);

  // Track when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionData?.sessionId) {
        const sessionDuration = Date.now() - (sessionData?.startTime || Date.now());
        
        dispatch(addEventToQueue({
          eventName: 'session_end',
          properties: {
            userId: user?.id,
            sessionId: sessionData?.sessionId,
            sessionDuration,
            lastPath: location.pathname,
            timestamp: new Date().toISOString(),
            pageViews: sessionData?.pageViews,
            interactions: sessionData?.interactionCount
          }
        }));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sessionData, user?.id, location.pathname, dispatch]);

  return null;
};

export default RouteTracker;